import React, { useRef, useState, useEffect } from "react";
import { FaPlayCircle, FaPauseCircle, FaVolumeUp } from "react-icons/fa";
import "./CustomAudioPlayer.css";

const speedOptions = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

function CustomAudioPlayer({ src }) {
  const audioRef = useRef(null);
  const progressRef = useRef(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [seekValue, setSeekValue] = useState(0); // for slider UI
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);

  useEffect(() => {
    const audio = audioRef.current;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration || 0);
    };

    const updateProgress = () => {
      if (!audio.seeking) {
        setCurrentTime(audio.currentTime);
        if (duration) {
          setSeekValue((audio.currentTime / duration) * 100);
        }
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", updateProgress);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", updateProgress);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [duration]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  const handlePlaybackRateChange = (rate) => {
    setPlaybackRate(rate);
    if (audioRef.current) {
      audioRef.current.playbackRate = rate;
    }
  };

  const handleSeekPreview = (e) => {
    const progress = parseFloat(e.target.value);
    if (!isNaN(progress)) {
      setSeekValue(progress);
    }
  };

  // On release: commit actual seek
  const handleSeekCommit = (e) => {
    const progress = parseFloat(e.target.value);
    if (progress === 0 && currentTime > 1) {
      return;
    }

    if (!isNaN(progress) && duration) {
      const newTime = (progress / 100) * duration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
      setSeekValue(progress);
    }
  };

  const handleVolumeChange = (e) => {
    const vol = parseFloat(e.target.value);
    setVolume(vol);
    audioRef.current.volume = vol;
  };

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (audio.paused) {
      audio.play();
      setIsPlaying(true);
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const s = Math.floor(seconds % 60)
      .toString()
      .padStart(2, "0");
    return `${m}:${s}`;
  };

  return (
    <div className="custom-audio-player">
      <audio ref={audioRef} src={src} preload="metadata" />
      <div className="controls-container">
        <button
          onClick={togglePlayPause}
          className={`play-pause-button ${isPlaying ? "pause" : "play"}`}
        >
          {isPlaying ? <FaPauseCircle size={20} /> : <FaPlayCircle size={20} />}
        </button>
        <FaVolumeUp size={16} className="volume-icon" />
        <input
          type="range"
          className="volume-slider"
          min="0"
          max="1"
          step="0.01"
          value={volume}
          onChange={handleVolumeChange}
        />
        <span className="volume-percentage">{Math.round(volume * 100)}%</span>
      </div>
      <div className="progress-container">
        <input
          ref={progressRef}
          type="range"
          className="progress-bar"
          min="0"
          max="100"
          step="0.1"
          value={seekValue}
          onInput={handleSeekPreview}
          onChange={handleSeekCommit} // on release
        />
        <span>
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
      </div>
      <div className="playback-rate-container">
        <span>Playback Speed:</span>
        <select
          className="speed-dropdown"
          value={playbackRate}
          onChange={(e) => handlePlaybackRateChange(parseFloat(e.target.value))}
        >
          {speedOptions.map((speed) => (
            <option key={speed} value={speed}>
              {speed}x
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

export default CustomAudioPlayer;
